import { Suspense, useState } from 'react';
import { Avatar, Badge, Button, Dropdown, Layout, Menu, Space, Spin, Tag } from 'antd';
import type { MenuProps } from 'antd';
import {
  AppstoreOutlined,
  AuditOutlined,
  BarChartOutlined,
  BookOutlined,
  CalendarOutlined,
  CloudUploadOutlined,
  CommentOutlined,
  CrownOutlined,
  CustomerServiceOutlined,
  DownOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  FilterOutlined,
  HomeOutlined,
  IdcardOutlined,
  KeyOutlined,
  LogoutOutlined,
  MessageOutlined,
  ReadOutlined,
  RollbackOutlined,
  SafetyCertificateOutlined,
  TagsOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  useCanHandleFeedback,
  useCanHandleRefunds,
  useCanManageLibrary,
  visibleMenuKeys,
} from '../auth/usePermissions';
import { ADMIN_ROLE_LABELS } from '../pages/admin-accounts/labels';
import { OwnPasswordModal } from '../pages/admin-accounts/OwnPasswordModal';
import {
  useCommunityStats,
  useFeedbackNewCount,
  useLibraryTodoCounts,
  useRefundPendingCount,
} from '../hooks/useMenuBadges';

interface MenuLeaf {
  key: string;
  label: string;
}

interface MenuEntry extends MenuLeaf {
  icon?: React.ReactNode;
  children?: MenuLeaf[];
}

/**
 * 菜单图标/文案表；可见性统一由 usePermissions.visibleMenuKeys 决定。
 * 「图书借阅」为 33 号 §7.1 的两级菜单：父级仅作分组，实际权限挂在叶子路由上。
 */
const MENU_ITEMS: MenuEntry[] = [
  { key: '/', icon: <HomeOutlined />, label: '概览' },
  { key: '/contents', icon: <FileTextOutlined />, label: '内容管理' },
  { key: '/tags', icon: <TagsOutlined />, label: '标签管理' },
  { key: '/kols', icon: <TeamOutlined />, label: 'KOL 管理', children: [
    { key: '/kols', label: 'KOL 列表' },
    { key: '/kols/applications', label: '认证审核' },
    { key: '/kols/articles', label: '投稿审核' },
  ] },
  { key: '/topics', icon: <AppstoreOutlined />, label: '专题管理' },
  { key: '/home-config', icon: <BookOutlined />, label: '首页配置' },
  { key: '/activities', icon: <CalendarOutlined />, label: '活动管理' },
  { key: '/community/moderation', icon: <SafetyCertificateOutlined />, label: '审核队列' },
  { key: '/community/reports', icon: <WarningOutlined />, label: '举报处理' },
  { key: '/community/sensitive-words', icon: <FilterOutlined />, label: '敏感词管理' },
  { key: '/community/identity', icon: <IdcardOutlined />, label: '社区身份' },
  { key: '/community/topics', icon: <CommentOutlined />, label: '社区话题' },
  { key: '/analytics', icon: <BarChartOutlined />, label: '运营数据' },
  { key: '/imports', icon: <CloudUploadOutlined />, label: '批量导入' },
  { key: '/users', icon: <IdcardOutlined />, label: '用户与会员' },
  { key: '/membership-plans', icon: <CrownOutlined />, label: '会员套餐' },
  { key: '/refunds', icon: <RollbackOutlined />, label: '退款处理' },
  {
    key: '/library',
    icon: <ReadOutlined />,
    label: '图书借阅',
    children: [
      { key: '/library/books', label: '图书管理' },
      { key: '/library/copies', label: '实体册' },
      { key: '/library/authors', label: '作者管理' },
      { key: '/library/donors', label: '捐赠人管理' },
      { key: '/library/borrowings', label: '借阅审批 / 借阅中' },
      { key: '/library/overdue', label: '逾期看板' },
      { key: '/library/reservations', label: '预约管理' },
      { key: '/library/settings', label: '书房配置' },
    ],
  },
  { key: '/agreements', icon: <FileProtectOutlined />, label: '协议管理' },
  { key: '/admin-accounts', icon: <TeamOutlined />, label: '后台账号' },
  { key: '/audit', icon: <AuditOutlined />, label: '审计日志' },
  { key: '/service-config', icon: <CustomerServiceOutlined />, label: '服务配置' },
  { key: '/feedback', icon: <MessageOutlined />, label: '用户反馈' },
];

/** 叶子路由（含分组子项），用于高亮当前菜单 */
function leafKeys(): string[] {
  return MENU_ITEMS.flatMap((item) =>
    item.children ? item.children.map((child) => child.key) : [item.key],
  );
}

/**
 * 菜单待处理角标（概览改造配套）：审核/举报/退款/反馈/借阅有积压时，
 * 在对应菜单右侧显示红色数字（0 自动隐藏）。
 * 数据与概览页待办共用同一批 queryKey（react-query 去重，不产生额外请求）。
 */
function badgeLabel(label: string, count?: number): React.ReactNode {
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        paddingRight: 2,
      }}
    >
      <span>{label}</span>
      {count ? (
        <Badge count={count} size="small" color="#D95C5C" overflowCount={99} />
      ) : null}
    </span>
  );
}

function selectedKey(pathname: string): string {
  if (pathname === '/') {
    return '/';
  }
  const matched = leafKeys()
    .filter((key) => key !== '/')
    .find((key) => pathname === key || pathname.startsWith(`${key}/`));
  return matched ?? '/';
}

/**
 * 后台整体布局：左侧导航 + 顶部账号信息。
 * 菜单按 usePermissions 的能力矩阵裁剪（客服只保留 /users 与 /feedback）；
 * 路由内容通过 Outlet 渲染，业务页面全部懒加载。
 */
export function AdminLayout(): React.ReactElement {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [passwordOpen, setPasswordOpen] = useState(false);

  const allowedKeys = visibleMenuKeys(admin?.roles ?? []);

  // 待处理角标计数（角色开关与后端 @AdminRoles 白名单一致，无权限不发请求）
  const roles = admin?.roles ?? [];
  const isModerator = roles.includes('SUPER_ADMIN') || roles.includes('MODERATOR');
  const canHandleRefunds = useCanHandleRefunds();
  const canHandleFeedback = useCanHandleFeedback();
  const canManageLibrary = useCanManageLibrary();

  const communityStats = useCommunityStats(isModerator).data;
  const refundTotal = useRefundPendingCount(canHandleRefunds).data?.total ?? 0;
  const feedbackTotal = useFeedbackNewCount(canHandleFeedback).data?.total ?? 0;
  const library = useLibraryTodoCounts(canManageLibrary).data;

  const borrowPending = library ? library.requested + library.approved + library.returnRequested : 0;
  const countMap: Record<string, number | undefined> = {
    '/community/moderation':
      communityStats && isModerator
        ? communityStats.pendingPosts + communityStats.pendingComments
        : undefined,
    '/community/reports': communityStats && isModerator ? communityStats.pendingReports : undefined,
    '/refunds': canHandleRefunds ? refundTotal : undefined,
    '/feedback': canHandleFeedback ? feedbackTotal : undefined,
    '/library/borrowings': canManageLibrary ? borrowPending : undefined,
    '/library/overdue': canManageLibrary ? library?.overdue : undefined,
    // 分组父级：子项之和（未覆盖状态不计入）
    '/library': canManageLibrary ? borrowPending + (library?.overdue ?? 0) : undefined,
  };

  // 分组菜单：叶子全部被裁剪掉时隐藏父级（父级本身不承载路由）
  const items: MenuProps['items'] = MENU_ITEMS.flatMap((item) => {
    if (!item.children) {
      return allowedKeys.includes(item.key)
        ? [{ key: item.key, icon: item.icon, label: badgeLabel(item.label, countMap[item.key]) }]
        : [];
    }
    const children = item.children.filter((child) => allowedKeys.includes(child.key));
    if (children.length === 0) {
      return [];
    }
    return [
      {
        key: item.key,
        icon: item.icon,
        label: badgeLabel(item.label, countMap[item.key]),
        children: children.map((child) => ({
          key: child.key,
          label: badgeLabel(child.label, countMap[child.key]),
        })),
      },
    ];
  });

  // 顶栏左侧显示当前所在页面（从选中菜单 key 反查标签，含分组叶子）
  const currentLabel = ((): string => {
    const key = selectedKey(location.pathname.replace(/^\/admin(?=\/|$)/, '') || '/');
    const entry = MENU_ITEMS.find(
      (item) => item.key === key || item.children?.some((child) => child.key === key),
    );
    if (!entry) {
      return '概览';
    }
    if (entry.children) {
      return entry.children.find((child) => child.key === key)?.label ?? entry.label;
    }
    return entry.label;
  })();

  const userMenuItems: MenuProps['items'] = [
    { key: 'password', icon: <KeyOutlined />, label: '修改密码' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
  ];

  const onUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'password') {
      setPasswordOpen(true);
    } else if (key === 'logout') {
      void logout();
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout.Sider theme="light" width={208} breakpoint="lg" collapsedWidth={64}>
        <div
          style={{
            height: 72,
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          <img
            src="/assets/brand/logo-horizontal.png"
            alt="鲤鱼来了"
            style={{ maxWidth: '100%', height: 52, objectFit: 'contain' }}
          />
        </div>
        <Menu
          mode="inline"
          selectedKeys={[
            // 挂在主域子路径 /admin 下时，location.pathname 会带前缀，需先剥掉再匹配菜单 key
            selectedKey(location.pathname.replace(/^\/admin(?=\/|$)/, '') || '/'),
          ]}
          defaultOpenKeys={['/library']}
          items={items}
          onClick={({ key }) => navigate(key)}
        />
      </Layout.Sider>
      <Layout>
        <Layout.Header
          style={{
            background: '#fff',
            borderBottom: '1px solid #EFEBF5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 24px',
            height: 64,
            lineHeight: 1,
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 600, color: '#3B126A' }}>{currentLabel}</span>
          <Space size={10}>
            <Avatar size={30} style={{ background: '#7A2CC7', fontSize: 14 }}>
              {(admin?.displayName ?? '游').trim().charAt(0) || '鲤'}
            </Avatar>
            <span style={{ color: '#43396B', fontSize: 14 }}>
              {admin?.displayName ?? '未登录'}
            </span>
            {admin?.roles.map((role) => (
              <Tag color="purple" key={role} style={{ marginInlineEnd: 0 }}>
                {ADMIN_ROLE_LABELS[role as keyof typeof ADMIN_ROLE_LABELS] ?? role}
              </Tag>
            ))}
            <Dropdown
              menu={{ items: userMenuItems, onClick: onUserMenuClick }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button type="text" size="small" icon={<DownOutlined style={{ fontSize: 10 }} />}>
                账号
              </Button>
            </Dropdown>
          </Space>
        </Layout.Header>
        <Layout.Content style={{ padding: 24, background: '#F7F6FA' }}>
          {/* 懒加载页面在布局内部显示加载态，避免整页闪烁 */}
          <Suspense
            fallback={
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
                <Spin size="large" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </Layout.Content>
      </Layout>
      <OwnPasswordModal open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </Layout>
  );
}
